import { useEffect, useState, useCallback } from 'react';
import { Drawer, Form, Button, Select, Space, Spin, Slider } from 'antd';


const formatPrecioAR = (n) => {
  const x = Number(n);
  if (!Number.isFinite(x)) return '$ —';
  return `$ ${x.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const CFiltroInicio = ({
  open,
  onClose,
  onApply,
  onReset,
  sucursalId,
  initialMarca,
  initialPrecioMin,
  initialPrecioMax
}) => {
  const [form] = Form.useForm();
  const [marcas, setMarcas] = useState([]);
  const [marcasLoading, setMarcasLoading] = useState(false);
  const [precioBounds, setPrecioBounds] = useState({ min: 0, max: 1 });
  const [precioRange, setPrecioRange] = useState([0, 1]);
  const [rangoLoading, setRangoLoading] = useState(false);

  useEffect(() => {
    if (!open || !sucursalId) return;
    let cancelled = false;
    const load = async () => {
      setMarcasLoading(true);
      try {
        const res = await fetch(
          `${process.env.REACT_APP_API_URL}/articulo-sucursal/marcas?sucursal=${encodeURIComponent(sucursalId)}`
        );
        if (!res.ok) throw new Error('marcas');
        const data = await res.json();
        if (!cancelled) setMarcas(Array.isArray(data.marcas) ? data.marcas : []);
      } catch {
        if (!cancelled) setMarcas([]);
      } finally {
        if (!cancelled) setMarcasLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [open, sucursalId]);

  useEffect(() => {
    if (!open || !sucursalId) return;
    let cancelled = false;
    const loadRango = async () => {
      setRangoLoading(true);
      try {
        const res = await fetch(
          `${process.env.REACT_APP_API_URL}/articulo-sucursal/precio-rango?sucursal=${encodeURIComponent(sucursalId)}`
        );
        if (!res.ok) throw new Error('precio-rango');
        const data = await res.json();
        const mn = Number(data.min);
        const mx = Number(data.max);
        if (cancelled) return;
        let bMin = Number.isFinite(mn) ? mn : 0;
        let bMax = Number.isFinite(mx) ? mx : bMin + 1;
        if (bMax <= bMin) bMax = bMin + 1;
        setPrecioBounds({ min: bMin, max: bMax });

        const hasMin = initialPrecioMin != null && initialPrecioMin !== '';
        const hasMax = initialPrecioMax != null && initialPrecioMax !== '';
        let lo = hasMin ? Number(initialPrecioMin) : bMin;
        let hi = hasMax ? Number(initialPrecioMax) : bMax;
        if (!Number.isFinite(lo)) lo = bMin;
        if (!Number.isFinite(hi)) hi = bMax;
        lo = Math.min(Math.max(lo, bMin), bMax);
        hi = Math.min(Math.max(hi, bMin), bMax);
        if (lo > hi) [lo, hi] = [hi, lo];
        setPrecioRange([lo, hi]);
      } catch {
        if (!cancelled) {
          setPrecioBounds({ min: 0, max: 1 });
          setPrecioRange([0, 1]);
        }
      } finally {
        if (!cancelled) setRangoLoading(false);
      }
    };
    loadRango();
    return () => {
      cancelled = true;
    };
  }, [open, sucursalId, initialPrecioMin, initialPrecioMax]);

  useEffect(() => {
    if (!open) return;
    form.setFieldsValue({
      aem_marca: initialMarca ?? undefined
    });
  }, [open, initialMarca, form]);

  const pctEnRango = useCallback(
    (valor) => {
      const { min, max } = precioBounds;
      const span = max - min || 1;
      return ((valor - min) / span) * 100;
    },
    [precioBounds]
  );

  const handleFinish = (values) => {
    const filters = Object.fromEntries(
      Object.entries(values).filter(([_, v]) => v !== undefined && v !== null && v !== '')
    );

    const bmin = precioBounds.min;
    const bmax = precioBounds.max;
    const [lo, hi] = precioRange;
    const tol = 0.01;
    const esRangoCompleto =
      Math.abs(lo - bmin) <= tol && Math.abs(hi - bmax) <= tol;

    if (!esRangoCompleto) {
      filters.precio_min = Math.round(lo);
      filters.precio_max = Math.round(hi);
    }

    onApply(filters);
    onClose();
  };

  const handleReset = () => {
    form.resetFields();
    setPrecioRange([precioBounds.min, precioBounds.max]);
    onReset?.();
    onClose();
  };

  const marcaOptions = marcas.map((m) => ({ value: m, label: m }));

  const leftPct = pctEnRango(precioRange[0]);
  const rightPct = pctEnRango(precioRange[1]);

  return (
    <Drawer
      title="Filtros de Artículos"
      placement="right"
      onClose={onClose}
      open={open}
      width={350}
    >
      <Form layout="vertical" form={form} onFinish={handleFinish}>
        <Form.Item name="aem_marca" label="Marca">
          <Select
            showSearch
            allowClear
            placeholder="Buscar por marca"
            loading={marcasLoading}
            optionFilterProp="label"
            filterOption={(input, option) =>
              (option?.label ?? '')
                .toString()
                .toLowerCase()
                .includes((input ?? '').toLowerCase())
            }
            options={marcaOptions}
            notFoundContent={marcasLoading ? <Spin size="small" /> : null}
            style={{ width: '100%' }}
          />
        </Form.Item>

        <div style={{ marginBottom: 10, color: '#434343', fontWeight: 500, fontSize: 14 }}>
          Gama de Precios
        </div>

        {rangoLoading ? (
          <div style={{ padding: '24px 0', textAlign: 'center' }}>
            <Spin />
          </div>
        ) : (
          <div style={{ marginBottom: 24, paddingTop: 4 }}>
            <div
              style={{
                position: 'relative',
                height: 22,
                marginBottom: 8,
                paddingLeft: 4,
                paddingRight: 4
              }}
            >
              <span
                style={{
                  position: 'absolute',
                  left: `clamp(2.75rem, ${leftPct}%, calc(100% - 2.75rem))`,
                  transform: 'translateX(-50%)',
                  whiteSpace: 'nowrap',
                  fontSize: 13,
                  color: '#262626',
                  fontWeight: 500,
                  zIndex: 2
                }}
              >
                {formatPrecioAR(precioRange[0])}
              </span>
            </div>
            <Slider
              range
              min={precioBounds.min}
              max={precioBounds.max}
              value={precioRange}
              onChange={setPrecioRange}
              tooltip={{ open: false }}
              styles={{
                rail: { backgroundColor: '#bfbfbf' },
                track: { backgroundColor: '#595959' }
              }}
            />
            <div
              style={{
                position: 'relative',
                height: 22,
                marginTop: 8,
                paddingLeft: 4,
                paddingRight: 4
              }}
            >
              <span
                style={{
                  position: 'absolute',
                  left: `clamp(2.75rem, ${rightPct}%, calc(100% - 2.75rem))`,
                  transform: 'translateX(-50%)',
                  whiteSpace: 'nowrap',
                  fontSize: 13,
                  color: '#262626',
                  fontWeight: 500,
                  zIndex: 2
                }}
              >
                {formatPrecioAR(precioRange[1])}
              </span>
            </div>
          </div>
        )}

        <Space style={{ marginTop: '1rem', width: '100%' }}>
          <Button onClick={handleReset} style={{ flex: 1 }}>
            Limpiar
          </Button>
          <Button type="primary" htmlType="submit" style={{ flex: 1, backgroundColor: '#e10a25', borderColor: '#e10a25' }}>
            Aplicar filtros
          </Button>
        </Space>
      </Form>
    </Drawer>
  );
};

export default CFiltroInicio;
